import React, { useState, useEffect, useCallback } from 'react';
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Row,
  Col,
  InputNumber,
  App,
  Typography,
  Spin,
  Popover,
} from 'antd';
import { SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import dayjs from 'dayjs';
import { City, Province, UserPropertyArea, ApiUser } from '../../types';
import * as countryCodes from 'country-codes-list';
import ReactCountryFlag from 'react-country-flag';

interface CountryCode {
  countryNameEn: string;
  countryCallingCode: string;
  countryCode: string; // ISO 2-letter code
}

// Correctly generate an array of objects from the library
const countryCodesObject = countryCodes.customList('countryCode', '{ "countryNameEn": "{countryNameEn}", "countryCallingCode": "+{countryCallingCode}", "countryCode": "{countryCode}" }');
const myCountryCodes: CountryCode[] = Object.values(countryCodesObject).map((jsonString: any) => JSON.parse(jsonString));
myCountryCodes.sort((a, b) => a.countryNameEn.localeCompare(b.countryNameEn));



const { Option } = Select;
const { TextArea } = Input;
const { Title } = Typography;

const LeadForm = () => {
  const { user, hasPermission } = useAuth();
  const [form] = Form.useForm();
  const { message } = App.useApp();

  useEffect(() => {
    console.log('Session User Data:', user);
  }, [user]);

  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(true);
  const [leadSourceType, setLeadSourceType] = useState<string | null>(null);
  const [selectedLeadSourceName, setSelectedLeadSourceName] = useState<string | null>(null);

  const [allUsers, setAllUsers] = useState<ApiUser[]>([]);
  const [propertyAreas, setPropertyAreas] = useState<any[]>([]);
  const [propertyTypes, setPropertyTypes] = useState<any[]>([]);

  const [leadPriorities, setLeadPriorities] = useState<any[]>([]);
  const [displayableLeadPriorities, setDisplayableLeadPriorities] = useState<any[]>([]);
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [leadSources, setLeadSources] = useState<any[]>([]);

  const [filteredCities, setFilteredCities] = useState<City[]>([]);
  const [assignableUsers, setAssignableUsers] = useState<ApiUser[]>([]);

  const [originalLead, setOriginalLead] = useState<any>(null);
  const [phoneValidation, setPhoneValidation] = useState<{ status: '' | 'error' | 'success' | 'warning' | 'validating', help: string | null }>({ status: '', help: null });
  const [duplicateLeadInfo, setDuplicateLeadInfo] = useState<any>(null);
  const [countryCode, setCountryCode] = useState('+62');

  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const canSelectPropertyArea = hasPermission('lead.property');
  const canAssign = hasPermission('lead.assign');

  const checkPhone = useCallback(async (phone: string) => {
    const fullPhone = `${countryCode.replace('+', '')}${phone}`;
    if (!phone) {
      setPhoneValidation({ status: '', help: null });
      setDuplicateLeadInfo(null);
      return;
    }
    // Skip validation if in edit mode and the phone number hasn't changed
    if (isEdit && originalLead && originalLead.phone === fullPhone) {
      setPhoneValidation({ status: 'success', help: null });
      setDuplicateLeadInfo(null);
      return;
    }

    setPhoneValidation({ status: 'validating', help: 'Memeriksa nomor telepon...' });
    setDuplicateLeadInfo(null);

    try {
      const payload: { phone: string; lead_id?: string } = { phone: fullPhone };
      if (isEdit && id) {
        payload.lead_id = id;
      }

      const response = await api.post('leads/check-phone', payload);
      if (response.data.data.exists) {
        setPhoneValidation({ status: 'error', help: null });
        setDuplicateLeadInfo(response.data.data.lead);
      } else {
        setPhoneValidation({ status: 'success', help: null });
        setDuplicateLeadInfo(null);
      }
    } catch (error: any) {
      // Assuming 404 means phone does not exist, which is a success case for the user
      if (error.response && error.response.status === 404) {
        setPhoneValidation({ status: 'success', help: null });
        setDuplicateLeadInfo(null);
      } else {
        // For other errors, don't block the user, just log it.
        console.error('Phone validation error:', error);
        setPhoneValidation({ status: '', help: null }); // Reset on server error
      }
    }
  }, [isEdit, originalLead, id, countryCode]);

  useEffect(() => {
    const activePriorities = leadPriorities.filter(p => p.is_active);
    setDisplayableLeadPriorities(activePriorities);
  }, [leadPriorities]);

  const loadData = useCallback(async () => {
    setFormLoading(true);
    try {
      const results = await Promise.allSettled([
        api.get('provinsi'),
        api.get('property-types', { params: { is_active: 'true' } }),
        api.get('lead-priority'),
        api.get('property-areas/role'),
        api.get('leads/users'),
      ]);

      const [
        provinceRes, typesRes, prioritiesRes, areasRes, usersRes
      ] = results;

      if (provinceRes.status === 'rejected') console.error("Failed to load provinces:", provinceRes.reason);
      if (typesRes.status === 'rejected') console.error("Failed to load property types:", typesRes.reason);
      if (prioritiesRes.status === 'rejected') console.error("Failed to load lead priorities:", prioritiesRes.reason);
      if (areasRes.status === 'rejected') console.error("Failed to load property areas:", areasRes.reason);
      if (usersRes.status === 'rejected') console.error("Failed to load users:", usersRes.reason);

      const usersData: ApiUser[] = usersRes.status === 'fulfilled' ? usersRes.value.data.data : [];
      setAllUsers(usersData);
      setPropertyAreas(areasRes.status === 'fulfilled' ? areasRes.value.data.data : []);
      setPropertyTypes(typesRes.status === 'fulfilled' ? typesRes.value.data.data : []);
      setLeadPriorities(prioritiesRes.status === 'fulfilled' ? prioritiesRes.value.data.data : []);

      const provinceData: Province[] = provinceRes.status === 'fulfilled' ? provinceRes.value.data.data : [];
      setProvinces(provinceData);
      const allCities: City[] = provinceData.flatMap((p: Province) =>
        p.kabupaten_kota_list.map((c: any) => ({ ...c, provinsi_id: p.id_provinsi }))
      );
      setCities(allCities);

      if (isEdit) {
        try {
            const leadResponse = await api.get(`leads/${id}`);
            const lead = leadResponse.data.data;
            setOriginalLead(lead);

            // Split phone into country code and number
            let number = lead.phone;
            const foundCode = myCountryCodes.find((c: CountryCode) => lead.phone.startsWith(c.countryCallingCode.replace('+', '')));
            if (foundCode) {
              setCountryCode(foundCode.countryCallingCode);
              number = lead.phone.substring(foundCode.countryCallingCode.replace('+', '').length);
            }

            if (lead.lead_source?.type) setLeadSourceType(lead.lead_source.type);
            if (lead.lead_source?.name) setSelectedLeadSourceName(lead.lead_source.name);
            if (lead.provinsi_id) {
              setFilteredCities(allCities.filter((c: City) => c.provinsi_id === lead.provinsi_id));
            }

            if (canSelectPropertyArea) {
                if(lead.property_area?.id) {
                    const filtered = usersData.filter((u: ApiUser) => u.user_property_areas?.some((upa: UserPropertyArea) => upa.property_area_id === lead.property_area.id));
                    setAssignableUsers(filtered);
                }
            } else {
                setAssignableUsers(usersData);
            }

            form.setFieldsValue({
              ...lead,
              phone: number, // use the split number
              lead_source_type: lead.lead_source?.type,
              property_area_id: lead.property_area?.id,
              property_type_id: lead.property_type?.id,
              assigned_to_id: lead.assigned_to?.id,
              lead_status_id: lead.status_lead?.id,
              lead_priority_id: lead.lead_priority?.id,
              next_follow_up: lead.next_follow_up ? dayjs(lead.next_follow_up) : null,
            });
        } catch (leadError) {
            console.error("Failed to load specific lead data:", leadError);
            message.error('Gagal memuat data lead spesifik.');
        }
      } else {
        if (!canSelectPropertyArea) setAssignableUsers(usersData);
      }

    } catch (error) {
      console.error("An unexpected error occurred during data loading:", error);
      message.error('Gagal memuat data form.');
    } finally {
      setFormLoading(false);
    }
  }, [id, isEdit, form, canSelectPropertyArea, message]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (user?.role?.name === 'sales_agent') {
      form.setFieldsValue({ assigned_to_id: user.id });
    }
  }, [user, form]);

  useEffect(() => {
    if (!leadSourceType) {
      setLeadSources([]);
      return;
    }
    const fetchLeadSources = async () => {
      try {
        const response = await api.get('lead-sources', { params: { type: leadSourceType, is_active: 'true' } });
        setLeadSources(response.data.data);
      } catch (error) { message.error(`Gagal memuat sumber lead`); }
    };
    fetchLeadSources();
  }, [leadSourceType, message]);

  const handleProvinceChange = (provinsiId: string) => {
    setFilteredCities(cities.filter((c: City) => c.provinsi_id === provinsiId));
    form.setFieldsValue({ kabupaten_kota_id: undefined });
  };

  const handleAreaChange = (areaId: string) => {
    const filtered = allUsers.filter((u: ApiUser) =>
        u.user_property_areas?.some((upa: UserPropertyArea) => upa.property_area_id === areaId)
    );
    setAssignableUsers(filtered);
    form.setFieldsValue({ assigned_to_id: undefined });
  };

  const handleLeadSourceTypeChange = (value: string) => {
    setLeadSourceType(value);
    setSelectedLeadSourceName(null);
    form.setFieldsValue({ lead_source_id: undefined, referal: undefined });
  };

  const handleLeadSourceChange = (value: string, option: any) => {
    setSelectedLeadSourceName(option.children);
    if (option.children !== 'Referral') {
      form.setFieldsValue({ referal: undefined });
    }
  };

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const selectedProvince = provinces.find((p: Province) => p.id_provinsi === values.provinsi_id);
      const selectedCity = cities.find((c: City) => c.id_kabupaten_kota === values.kabupaten_kota_id);

      const submitData = { ...values,
        phone: `${countryCode.replace('+', '')}${values.phone}`,
        next_follow_up: values.next_follow_up ? values.next_follow_up.toISOString() : null,
        provinsi_name: selectedProvince?.nama_provinsi,
        kabupaten_kota_name: selectedCity?.nama_kabupaten_kota,
      };

      if (isEdit && originalLead) {
        submitData.property_area_id = originalLead.property_area?.id || values.property_area_id;
        submitData.assigned_to_id = originalLead.assigned_to?.id || values.assigned_to_id;
      }

      // If the user is not a manager, we might need to auto-assign a property area
      if (!canSelectPropertyArea && user) {
        // For non-managers, automatically assign their first property area
        submitData.property_area_id = user.user_property_areas?.[0]?.property_area_id;
      }

      delete submitData.lead_source_type;
      delete submitData.other_lead_source;

      const request = isEdit ? api.put(`leads/${id}`, submitData) : api.post('leads', submitData);
      await request;
      message.success(`Lead berhasil ${isEdit ? 'diperbarui' : 'dibuat'}`);
      navigate('/leads/data');
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Gagal menyimpan lead');
    } finally {
      setLoading(false);
    }
  };

  if (formLoading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div>;
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            {isEdit ? 'Edit Lead' : 'Tambah Lead'}
          </Title>
        </Col>
        <Col>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/leads/data')}>
            Kembali
          </Button>
        </Col>
      </Row>
      <Card>
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={[16, 0]}>
            <Col xs={24}>
              <Title level={4}>Informasi Personal</Title>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Nama Lengkap" name="full_name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="No. Telp/Whatsapp"
                name="phone"
                rules={[
                  { required: true, message: 'Nomor telepon wajib diisi' },
                  {
                    pattern: /^[0-9]+$/,
                    message: 'Input hanya boleh berisi angka.',
                  },
                ]}
                validateStatus={phoneValidation.status}
                help={phoneValidation.help}
              >
                <Input
                  addonBefore={
                    <Select
                      labelInValue
                      value={{
                        value: countryCode,
                        label: (
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <ReactCountryFlag
                              countryCode={myCountryCodes.find(c => c.countryCallingCode === countryCode)?.countryCode || 'ID'}
                              svg
                              style={{ marginRight: '8px' }}
                            />
                            {countryCode}
                          </div>
                        )
                      }}
                      onChange={(option: any) => setCountryCode(option.value)}
                      showSearch
                      style={{ width: 110 }}
                      dropdownStyle={{ minWidth: 350 }}
                      filterOption={(input, option: any) =>
                        String(option.searchText).toLowerCase().includes(input.toLowerCase())
                      }
                      options={myCountryCodes.map((c: CountryCode) => ({
                        key: c.countryCode,
                        value: c.countryCallingCode,
                        label: (
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <ReactCountryFlag countryCode={c.countryCode} svg style={{ marginRight: '8px' }} />
                            {c.countryNameEn} ({c.countryCallingCode})
                          </div>
                        ),
                        searchText: `${c.countryNameEn} ${c.countryCallingCode}`
                      }))}
                    />
                  }
                  type="tel"
                  onBlur={(e) => checkPhone(e.target.value)}
                  onChange={(e) => {
                    const { value } = e.target;
                    form.setFieldsValue({ phone: value.replace(/[^0-9]/g, '') });
                  }}
                />
              </Form.Item>
              {duplicateLeadInfo && (
                <div style={{ marginTop: '-20px', marginBottom: 24 }}>
                  <Typography.Text type="danger">
                    Nomor ini sudah terdaftar a/n {duplicateLeadInfo.full_name}.{' '}
                    <Popover
                      content={
                        <div style={{ maxWidth: 300 }}>
                          <Row>
                            <Col span={10}><Typography.Text type="secondary">Property Area</Typography.Text></Col>
                            <Col span={14}><Typography.Text>{duplicateLeadInfo.property_area || '-'}</Typography.Text></Col>
                          </Row>
                          <Row>
                            <Col span={10}><Typography.Text type="secondary">Sales</Typography.Text></Col>
                            <Col span={14}><Typography.Text>{duplicateLeadInfo.assignedTo || 'Belum di-assign'}</Typography.Text></Col>
                          </Row>
                          <Row>
                            <Col span={10}><Typography.Text type="secondary">Status (HOT/COLD)</Typography.Text></Col>
                            <Col span={14}><Typography.Text>{(duplicateLeadInfo.follow_ups && duplicateLeadInfo.follow_ups.length > 0) ? duplicateLeadInfo.follow_ups[0].status : '-'}</Typography.Text></Col>
                          </Row>
                          <Row>
                            <Col span={10}><Typography.Text type="secondary">Status Follow Up</Typography.Text></Col>
                            <Col span={14}><Typography.Text>{duplicateLeadInfo.status || 'Belum ada'}</Typography.Text></Col>
                          </Row>
                          <Row>
                            <Col span={10}><Typography.Text type="secondary">Dibuat Oleh</Typography.Text></Col>
                            <Col span={14}><Typography.Text>{duplicateLeadInfo.created_by || '-'}</Typography.Text></Col>
                          </Row>
                        </div>
                      }
                      title={<Typography.Text strong>Detail Lead</Typography.Text>}
                      trigger="click"
                    >
                      <Button type="link" style={{ padding: 0, height: 'auto' }}>
                        Lihat Detail
                      </Button>
                    </Popover>
                  </Typography.Text>
                </div>
              )}
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Email" name="email" rules={[{ type: 'email' }]}>
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Title level={4} style={{ marginTop: 24 }}>Informasi Lokasi</Title>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Provinsi" name="provinsi_id" rules={[{ required: true }]}>
                <Select showSearch onChange={handleProvinceChange} filterOption={(input, option) => (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())}>
                  {provinces.map((p: Province) => <Option key={p.id_provinsi} value={p.id_provinsi}>{p.nama_provinsi}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Kabupaten/Kota" name="kabupaten_kota_id" rules={[{ required: true }]}>
                <Select showSearch disabled={filteredCities.length === 0} filterOption={(input, option) => (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())}>
                  {filteredCities.map((c: City) => <Option key={c.id_kabupaten_kota} value={c.id_kabupaten_kota}>{c.nama_kabupaten_kota}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label="Alamat" name="alamat">
                <TextArea rows={3} />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Title level={4} style={{ marginTop: 24 }}>Informasi Lead</Title>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Tipe Sumber Lead" name="lead_source_type" rules={[{ required: true }]}>
                <Select onChange={handleLeadSourceTypeChange}>
                  <Option value="online">Online</Option>
                  <Option value="offline">Offline</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Sumber Lead" name="lead_source_id" rules={[{ required: true }]}>
                <Select
                  showSearch
                  disabled={!leadSourceType}
                  onChange={handleLeadSourceChange}
                  filterOption={(input, option) => (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())}
                >
                  {leadSources.map(s => <Option key={s.id} value={s.id}>{s.name}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            {selectedLeadSourceName === 'Referral' && (
              <Col xs={24} md={12}>
                <Form.Item label="Nama Referral" name="referal" rules={[{ required: true, message: 'Nama referral wajib diisi' }]}>
                  <Input />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} md={12}>
              <Form.Item label="Tipe Properti" name="property_type_id" rules={[{ required: true }]}>
                <Select>
                  {propertyTypes.map((t: any) => <Option key={t.id} value={t.id}>{t.name}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Budget" name="budget">
                <InputNumber style={{ width: '100%' }} formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={(v) => v ? v.replace(/\$\s?|(,*)/g, '') : ''} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item label="Prioritas" name="lead_priority_id" rules={[{ required: true }]}>
                <Select>
                  {displayableLeadPriorities.map(p => <Option key={p.id} value={p.id}>{p.name}</Option>)}
                </Select>
              </Form.Item>
            </Col>

            {canSelectPropertyArea && (
              <Col xs={24} md={12}>
                <Form.Item label="Property Area" name="property_area_id" rules={[{ required: true }]}>
                  <Select placeholder="Pilih property area" onChange={handleAreaChange} disabled={isEdit}>
                    {propertyAreas.map((area: any) => <Option key={area.id} value={area.id}>{area.name}</Option>)}
                  </Select>
                </Form.Item>
              </Col>
            )}

            {canAssign && (
              <Col xs={24} md={12}>
                <Form.Item
                  label="Assigned To"
                  name="assigned_to_id"
                >
                  <Select
                    placeholder="Pilih user"
                    allowClear
                    disabled={isEdit || user?.role?.name === 'sales_agent'}
                  >
                    {assignableUsers.map((u: ApiUser) => <Option key={u.id} value={u.id}>{u.full_name}</Option>)}
                  </Select>
                </Form.Item>
                { user?.role?.name !== 'sales_agent' && (
                  <Typography.Text type="warning" style={{ marginTop: '-20px', display: 'block' }}>
                    Jika dikosongkan, sistem akan menugaskan lead secara acak ke sales di area terkait.
                  </Typography.Text>
                )}
              </Col>
            )}

            <Col xs={24}>
              <Form.Item style={{ marginTop: 32 }}>
                <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />} size="large">
                  {isEdit ? 'Update Lead' : 'Simpan Lead'}
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default LeadForm;
